import React, { useState, useEffect, useRef } from 'react';
import { useContracts } from '../../hooks/useContract';
import { MATRIX_LEVELS } from '../../config/contracts';
import Card from '../common/Card';
import MiniLoader from '../common/MiniLoader';

const MatrixTree = ({ userId, packageId }) => {
  const { storage } = useContracts();
  const [levels, setLevels] = useState([]);
  const [uplineId, setUplineId] = useState(0);
  const [teamSize, setTeamSize] = useState(0);
  const [loading, setLoading] = useState(false);
  const runIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!userId || !packageId) return;
      const myRun = ++runIdRef.current;
      setLoading(true);
      try {
        // Fetch upline, team size, and first 5 levels in parallel
        const [upline, size, ...levelResults] = await Promise.all([
          storage.packageMatrixUpline(packageId, userId),
          storage.packageMatrixTeamSize(packageId, userId),
          ...Array.from({ length: MATRIX_LEVELS }, (_, i) =>
            storage.getMatrixLevelMembers(packageId, userId, i + 1)
          )
        ]);
        if (cancelled || myRun !== runIdRef.current) return;
        setUplineId(upline.toNumber ? upline.toNumber() : Number(upline));
        setTeamSize(size.toNumber ? size.toNumber() : Number(size));
        setLevels(levelResults.map((arr) => arr.map((x) => x.toNumber ? x.toNumber() : Number(x))));
      } catch (_) { /* ignore */ }
      finally {
        if (myRun === runIdRef.current) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [userId, packageId]);

  if (loading) {
    return (
      <Card title={`Matrix — Package ${packageId}`}>
        <div className="text-center" style={{ padding: 40 }}>
          <MiniLoader />
        </div>
      </Card>
    );
  }

  return (
    <div>
      <div className="grid grid-3 mb-md">
        <div className="stat">
          <span className="stat-label">Upline</span>
          <span className="stat-value">{uplineId > 0 ? `#${uplineId}` : '-'}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Total Team</span>
          <span className="stat-value text-gradient">{teamSize}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Matrix Levels</span>
          <span className="stat-value">{MATRIX_LEVELS}</span>
        </div>
      </div>

      <Card title={`Levels — Package ${packageId}`}>
        <div className="flex flex-col gap-sm">
          {levels.map((members, idx) => {
            const level = idx + 1;
            const maxAtLevel = 1 << level;
            const filled = members.length;
            const percent = Math.min(100, (filled / maxAtLevel) * 100);
            return (
              <div key={level}>
                <div className="flex flex-between mb-sm" style={{ fontSize: 13 }}>
                  <span>
                    <strong>Level {level}</strong>
                    <span className="text-muted" style={{ marginLeft: 8 }}>
                      {filled} / {maxAtLevel}
                    </span>
                  </span>
                  <span className={filled >= maxAtLevel ? 'badge badge-success' : 'text-muted'} style={{ fontSize: 11 }}>
                    {filled >= maxAtLevel ? 'Complete' : `${percent.toFixed(0)}%`}
                  </span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${percent}%` }}></div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

export default MatrixTree;
